import { useEffect } from "react";
import { Check, X, Star, ShieldCheck } from "@phosphor-icons/react";

// Lightweight SEO helper — sets title + meta description per page.
export function useSeo(title, description) {
  useEffect(() => {
    const prev = document.title;
    document.title = title;
    let tag = document.querySelector('meta[name="description"]');
    if (!tag) {
      tag = document.createElement("meta");
      tag.setAttribute("name", "description");
      document.head.appendChild(tag);
    }
    const prevDesc = tag.getAttribute("content");
    tag.setAttribute("content", description);
    return () => { document.title = prev; if (prevDesc) tag.setAttribute("content", prevDesc); };
  }, [title, description]);
}

export const Container = ({ children, className = "" }) => (
  <div className={`max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-[120px] ${className}`}>{children}</div>
);

export function PlanHero({ accent, bgFrom, bgTo, eyebrow, title, highlight, subtitle, children, stats }) {
  return (
    <section
      className="relative overflow-hidden pt-14 pb-16 sm:pt-20 sm:pb-20"
      style={{ background: `linear-gradient(160deg, ${bgFrom} 0%, ${bgTo} 55%, #ffffff 100%)` }}
    >
      <div aria-hidden className="absolute -top-24 -right-24 w-96 h-96 rounded-full blur-3xl opacity-40" style={{ background: accent }} />
      <div aria-hidden className="absolute -bottom-32 -left-20 w-96 h-96 rounded-full blur-3xl opacity-20" style={{ background: accent }} />
      <Container className="relative">
        <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/70 backdrop-blur border border-white text-[11px] font-black uppercase tracking-[0.18em]" style={{ color: accent }}>
          <Star size={12} weight="fill" /> {eyebrow}
        </span>
        <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05] text-[#09090B] font-heading max-w-4xl">
          {title} <span style={{ color: accent }}>{highlight}</span>
        </h1>
        <p className="mt-5 text-base sm:text-lg text-[#3F3F46] max-w-2xl leading-relaxed">{subtitle}</p>
        <div className="mt-8 flex flex-wrap gap-3">{children}</div>
        {stats && (
          <div className="mt-12 grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-3xl">
            {stats.map((s) => (
              <div key={s.label} className="bg-white/70 backdrop-blur rounded-2xl border border-white px-4 py-4">
                <div className="text-2xl sm:text-3xl font-black font-['IBM_Plex_Mono']" style={{ color: accent }}>{s.value}</div>
                <div className="text-[11px] font-bold uppercase tracking-wider text-zinc-500 mt-1">{s.label}</div>
              </div>
            ))}
          </div>
        )}
      </Container>
    </section>
  );
}

export function SectionTitle({ eyebrow, title, sub, accent = "#6043EF", center = false }) {
  return (
    <div className={`mb-10 ${center ? "text-center mx-auto max-w-2xl" : "max-w-3xl"}`}>
      {eyebrow && <span className="text-[11px] font-black uppercase tracking-[0.2em]" style={{ color: accent }}>{eyebrow}</span>}
      <h2 className="mt-2 text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#09090B] font-heading">{title}</h2>
      {sub && <p className="mt-3 text-base text-[#52525B] leading-relaxed">{sub}</p>}
    </div>
  );
}

export const CheckItem = ({ children, accent = "#10B981" }) => (
  <li className="flex items-start gap-2.5 text-sm text-[#3F3F46] leading-relaxed">
    <Check size={16} weight="bold" className="mt-0.5 shrink-0" style={{ color: accent }} />
    <span>{children}</span>
  </li>
);

export const CrossItem = ({ children }) => (
  <li className="flex items-start gap-2.5 text-sm text-zinc-400 leading-relaxed line-through decoration-zinc-300">
    <X size={16} weight="bold" className="mt-0.5 shrink-0 text-zinc-300" />
    <span>{children}</span>
  </li>
);

export function StepCard({ n, title, body, accent }) {
  return (
    <div className="relative bg-white rounded-2xl border border-zinc-100 p-6 shadow-sm hover:shadow-md transition-shadow">
      <div className="w-10 h-10 rounded-xl flex items-center justify-center font-black text-white text-sm" style={{ backgroundColor: accent }}>{n}</div>
      <h3 className="mt-4 text-base font-bold text-[#09090B]">{title}</h3>
      <p className="mt-1.5 text-sm text-[#52525B] leading-relaxed">{body}</p>
    </div>
  );
}

export function FeatureCard({ icon: Icon, title, body, accent }) {
  return (
    <div className="bg-white rounded-2xl border border-zinc-100 p-6 shadow-sm hover:-translate-y-1 transition-transform duration-200">
      <span className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${accent}1A` }}>
        <Icon size={22} weight="fill" style={{ color: accent }} />
      </span>
      <h3 className="mt-4 text-base font-bold text-[#09090B]">{title}</h3>
      <p className="mt-1.5 text-sm text-[#52525B] leading-relaxed">{body}</p>
    </div>
  );
}

export function FaqList({ items, accent = "#6043EF" }) {
  return (
    <div className="space-y-3 max-w-3xl">
      {items.map((f, i) => (
        <details key={i} className="group bg-white rounded-xl border border-zinc-100 p-5 [&_summary]:cursor-pointer" data-testid={`faq-${i}`}>
          <summary className="flex items-center justify-between gap-4 text-sm font-bold text-[#09090B] list-none">
            {f.q}
            <span className="text-lg leading-none transition-transform group-open:rotate-45" style={{ color: accent }}>+</span>
          </summary>
          <p className="mt-3 text-sm text-[#52525B] leading-relaxed">{f.a}</p>
        </details>
      ))}
    </div>
  );
}

export const RefundBadge = ({ text, accent = "#10B981" }) => (
  <span className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full" style={{ backgroundColor: `${accent}1A`, color: accent }}>
    <ShieldCheck size={14} weight="fill" /> {text}
  </span>
);

export const money = (n) => `₹${Number(n).toLocaleString("en-IN")}`;
